/**
 * Package Editor Page
 * Two-panel layout: left = package items, right = product search
 * Top: basic info, bottom: pricing config + live calculator
 */

import type {
  AddPackageItemDTO,
  PackageItem as ApiPackageItem,
  CreatePackageDTO,
  PackageItemCategory,
  PackageItemType,
  PackageLinkingMode,
  PackageScope,
  PackageType,
  PricingModel,
  UpdatePackageDTO,
  UpdatePackageItemDTO,
} from '@/api/packages';
import { getProducts, type Product } from '@/api/products';
import { Button, Card, CardContent, Input } from '@/components/ui';
import {
  useAddPackageItem,
  useCreatePackage,
  useForkPackage,
  usePackage,
  useRemovePackageItem,
  useUpdatePackage,
  useUpdatePackageItem,
} from '@/hooks/use-packages';
import { formatHuf } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  Badge,
  SharedPackageConflictDialog,
  type SharedPackageResolutionChoice,
} from '@kgc/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PackageScopeSection, type EquipmentCategoryValue } from './components/PackageScopeSection';
import type { PickedEquipment } from './components/RentalEquipmentPicker';

// ─── Constants ───────────────────────────────────────────────────────────────

const PACKAGE_TYPES: PackageType[] = [
  'RENTAL',
  'SALES',
  'SERVICE',
  'MIXED',
  'PROMOTIONAL',
  'FRANCHISE',
];

const PRICING_MODELS: PricingModel[] = ['FIXED', 'DISCOUNTED', 'TIERED'];

const SCOPES: PackageScope[] = ['CENTRAL', 'LOCAL'];

const ALL_ITEM_TYPES: PackageItemType[] = ['PRODUCT', 'SERVICE', 'RENTAL_EQUIPMENT'];

const ITEM_TYPE_COLORS: Record<PackageItemType, string> = {
  PRODUCT: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  SERVICE: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  RENTAL_EQUIPMENT: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  ACCESSORY: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  SERVICE_ITEM: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
};

// ─── Editor Item (local UI type, enriched from API PackageItem) ─────────────

interface EditorItem {
  id: string;
  packageId: string;
  itemType: PackageItemType;
  referenceId: string;
  referenceName: string;
  referenceSku?: string;
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  sortOrder: number;
  // STORY-1205 — csomag-tétel editor mezők (ADR-118 Am.2 D-1)
  category: PackageItemCategory;
  defaultQuantity: number;
  isQuantityEditable: boolean;
  visibleOnWebshop: boolean;
}

/** Map API PackageItem → EditorItem for display/editing */
function mapApiItemsToEditorItems(items: ApiPackageItem[]): EditorItem[] {
  return items.map(item => ({
    id: item.id,
    packageId: item.packageId,
    itemType: item.itemType as PackageItemType,
    referenceId: item.productId ?? item.rentalEquipmentId ?? '',
    referenceName: item.notes ?? item.productId ?? item.rentalEquipmentId ?? item.itemType,
    referenceSku: undefined,
    quantity: item.quantity,
    unitPrice: item.unitPrice ?? 0,
    discountPercent: 0,
    sortOrder: item.sortOrder,
    // STORY-1205 — backend default-okkal (a régi rekordok is konzisztensek)
    category: item.category ?? 'RENTABLE',
    defaultQuantity: item.defaultQuantity ?? 1,
    isQuantityEditable: item.isQuantityEditable ?? false,
    visibleOnWebshop: item.visibleOnWebshop ?? false,
  }));
}

// ─── Form State ──────────────────────────────────────────────────────────────

interface PackageForm {
  code: string;
  name: string;
  description: string;
  type: PackageType;
  scope: PackageScope;
  pricingModel: PricingModel;
  fixedPrice: string;
  discountPercent: string;
  validFrom: string;
  validUntil: string;
  linkedEquipmentCategories: EquipmentCategoryValue[];
  linkingMode: PackageLinkingMode;
  linkedEquipment: PickedEquipment[];
}

const INITIAL_FORM: PackageForm = {
  code: '',
  name: '',
  description: '',
  type: 'MIXED',
  scope: 'LOCAL',
  pricingModel: 'FIXED',
  fixedPrice: '',
  discountPercent: '',
  validFrom: '',
  validUntil: '',
  linkedEquipmentCategories: [],
  linkingMode: 'CATEGORY',
  linkedEquipment: [],
};

// ─── Component ───────────────────────────────────────────────────────────────

export function PackageEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copyFrom');

  const isEditMode = Boolean(id);
  const loadId = id ?? copyFromId ?? undefined;

  // Existing package data (for edit or copy)
  const { pkg: existingPkg, isLoading: pkgLoading } = usePackage(loadId);

  // Mutations
  const { mutate: doCreate, isLoading: createLoading } = useCreatePackage();
  const { mutate: doUpdate, isLoading: updateLoading } = useUpdatePackage();
  const { mutate: doAddItem } = useAddPackageItem();
  const { mutate: doRemoveItem } = useRemovePackageItem();
  const { mutate: doUpdateItem } = useUpdatePackageItem();
  const { mutate: doFork } = useForkPackage();

  // Form state
  const [form, setForm] = useState<PackageForm>(INITIAL_FORM);
  const [items, setItems] = useState<EditorItem[]>([]);

  // Syncing guard to prevent double-save
  const [isSyncing, setIsSyncing] = useState(false);

  // STORY-1206 Q4 — detect-first conflict dialog (shared master save-gate)
  const [conflictOpen, setConflictOpen] = useState(false);
  // Stage-2 (Javo-ratify): a "Csak erre" fork cél-gépmodellje a dialog-pickerből
  const [copyTargetId, setCopyTargetId] = useState<string | null>(null);

  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Populate form when editing or copying
  useEffect(() => {
    if (existingPkg) {
      setForm({
        code: copyFromId ? `${existingPkg.packageCode}-COPY` : existingPkg.packageCode,
        name: copyFromId ? `${existingPkg.name} (${t('packages.copy')})` : existingPkg.name,
        description: existingPkg.description ?? '',
        type: existingPkg.type,
        scope: existingPkg.scope,
        pricingModel: existingPkg.pricingModel,
        fixedPrice: existingPkg.fixedPrice?.toString() ?? '',
        discountPercent: existingPkg.discountPercent?.toString() ?? '',
        validFrom: existingPkg.validFrom?.split('T')[0] ?? '',
        validUntil: existingPkg.validTo?.split('T')[0] ?? '',
        linkedEquipmentCategories: (existingPkg.linkedEquipmentCategories ??
          []) as EquipmentCategoryValue[],
        linkingMode: existingPkg.linkingMode ?? 'CATEGORY',
        linkedEquipment: (existingPkg.linkedProducts ?? []).map(lp => ({
          productId: lp.productId,
          name: lp.product.name,
          sku: lp.product.sku,
        })),
      });
      setItems(mapApiItemsToEditorItems(existingPkg.items ?? []));
    }
  }, [existingPkg, copyFromId, t]);

  // Product search handler
  const handleProductSearch = useCallback(async () => {
    if (!productSearch.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const response = await getProducts({ search: productSearch, pageSize: 20 });
      setSearchResults(response.data);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [productSearch]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      if (!controller.signal.aborted) {
        void handleProductSearch();
      }
    }, 300);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [handleProductSearch]);

  // Update form field
  const setField = <K extends keyof PackageForm>(key: K, value: PackageForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Add product to items
  const handleAddProduct = (product: Product) => {
    const alreadyAdded = items.some(item => item.referenceId === product.id);
    if (alreadyAdded) return;

    const newItem: EditorItem = {
      id: `temp-${Date.now()}`,
      packageId: id ?? '',
      itemType: 'PRODUCT',
      referenceId: product.id,
      referenceName: product.name,
      referenceSku: product.sku,
      quantity: 1,
      unitPrice: product.sellingPriceGross,
      discountPercent: 0,
      sortOrder: items.length,
      // STORY-1205 — backend default-okkal
      category: 'RENTABLE',
      defaultQuantity: 1,
      isQuantityEditable: false,
      visibleOnWebshop: false,
    };
    setItems(prev => [...prev, newItem]);
  };

  // STORY-1205 — egy editor-item mezőjének frissítése (category/defaultQuantity/toggle-ök).
  // CONSIGNMENT besorolásnál a visibleOnWebshop-ot a backend DB-trigger FALSE-ra forceolja
  // (ADR-118 Am.2 D-4) → a UI is tükrözi: a kapcsoló disabled + false CONSIGNMENT-nél.
  const handleUpdateItemField = <K extends keyof EditorItem>(
    itemId: string,
    field: K,
    value: EditorItem[K]
  ) => {
    setItems(prev =>
      prev.map(i => {
        if (i.id !== itemId) return i;
        const next = { ...i, [field]: value };
        if (field === 'category' && value === 'CONSIGNMENT') {
          next.visibleOnWebshop = false;
        }
        return next;
      })
    );
  };

  // Remove item
  const handleRemoveItem = (itemId: string) => {
    setItems(prev => prev.filter(i => i.id !== itemId));
  };

  // Update item quantity
  const handleUpdateItemQty = (itemId: string, qty: number) => {
    setItems(prev => prev.map(i => (i.id === itemId ? { ...i, quantity: Math.max(1, qty) } : i)));
  };

  // Move item (reorder)
  const handleMoveItem = (itemId: string, direction: 'up' | 'down') => {
    setItems(prev => {
      const idx = prev.findIndex(i => i.id === itemId);
      if (idx < 0) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const newItems = [...prev];
      const temp = newItems[idx];
      const target = newItems[newIdx];
      if (!temp || !target) return prev;
      newItems[idx] = target;
      newItems[newIdx] = temp;
      return newItems;
    });
  };

  // For RENTAL packages, RENTAL_EQUIPMENT items are managed via the equipment module — hide them here
  const availableItemTypes = useMemo(
    () =>
      form.type === 'RENTAL'
        ? ALL_ITEM_TYPES.filter(t => t !== 'RENTAL_EQUIPMENT')
        : ALL_ITEM_TYPES,
    [form.type]
  );

  // Calculator
  const calculator = useMemo(() => {
    const itemsTotal = items.reduce((sum, item) => {
      const discounted = item.unitPrice * (1 - item.discountPercent / 100);
      return sum + discounted * item.quantity;
    }, 0);

    let packagePrice = itemsTotal;
    if (form.pricingModel === 'FIXED' && form.fixedPrice) {
      packagePrice = parseFloat(form.fixedPrice);
    } else if (form.pricingModel === 'DISCOUNTED' && form.discountPercent) {
      packagePrice = itemsTotal * (1 - parseFloat(form.discountPercent) / 100);
    }

    const savings = itemsTotal > 0 ? ((itemsTotal - packagePrice) / itemsTotal) * 100 : 0;

    return { itemsTotal, packagePrice, savings };
  }, [items, form.pricingModel, form.fixedPrice, form.discountPercent]);

  // STORY-1206 — hány géphez van linkelve a perzisztált master (EQUIPMENT-mód).
  // CATEGORY-módban nincs gép-kötés → 0 (banner + dialog inaktív).
  // A küszöb-logika a @kgc/package-selection `needsConflictDialog`-jával azonos
  // (>= 2) — direkt import nélkül, mert a package root-ja NestJS-t húzna a bundle-be.
  const sharedEquipmentCount =
    isEditMode && existingPkg?.linkingMode === 'EQUIPMENT'
      ? (existingPkg.linkedProducts?.length ?? 0)
      : 0;

  // Save handler — Q4 detect-first gate: shared masteren előbb a conflict-dialog.
  const handleSave = async () => {
    if (isSyncing) return;
    if (isEditMode && sharedEquipmentCount >= 2) {
      setConflictOpen(true);
      return;
    }
    await performSave();
  };

  // Q4 dialog-választás feloldása.
  const handleConflictResolve = async (choice: SharedPackageResolutionChoice) => {
    if (choice === 'apply-to-all') {
      // A dialog nyitva marad a mentés alatt (loading-state), siker után a
      // navigate úgyis unmountol; hibánál visszakapja a választást a user.
      await performSave();
      setConflictOpen(false);
      return;
    }
    if (choice === 'copy-for-this') {
      // Stage-2 (#412 fork-seam): copy-on-write fork a kiválasztott gépmodellre.
      // A szerkesztési diff NEM kerül át a variantra (ratified MVP-szemantika) —
      // siker után a variantra navigálunk, ott folytatható a szerkesztés.
      if (!id || !copyTargetId) return;
      try {
        const result = await doFork(id, { productId: copyTargetId });
        setConflictOpen(false);
        navigate(`/packages/${result.newPackageId}`);
      } catch {
        // Hiba: a dialog nyitva marad, a user újra próbálhat vagy Mégse.
      }
      return;
    }
    setConflictOpen(false);
  };

  const performSave = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    try {
      if (isEditMode && id) {
        const updateData: UpdatePackageDTO = {
          name: form.name,
          description: form.description || undefined,
          type: form.type,
          scope: form.scope,
          pricingModel: form.pricingModel,
          fixedPrice: form.fixedPrice ? parseFloat(form.fixedPrice) : undefined,
          discountPercent: form.discountPercent ? parseFloat(form.discountPercent) : undefined,
          validFrom: form.validFrom || undefined,
          validTo: form.validUntil || undefined,
          linkingMode: form.type === 'RENTAL' ? form.linkingMode : undefined,
          linkedEquipmentCategories:
            form.type === 'RENTAL' && form.linkingMode === 'CATEGORY'
              ? form.linkedEquipmentCategories
              : [],
          linkedProductIds:
            form.type === 'RENTAL' && form.linkingMode === 'EQUIPMENT'
              ? form.linkedEquipment.map(e => e.productId)
              : [],
        };
        await doUpdate(id, updateData);

        // Sync items: remove old items that are no longer present, add new ones
        const existingApiItems = existingPkg?.items ?? [];
        const existingItemIds = new Set(existingApiItems.map(i => i.id));
        const currentItemIds = new Set(items.map(i => i.id));

        // Remove deleted items
        for (const existingItem of existingApiItems) {
          if (!currentItemIds.has(existingItem.id)) {
            await doRemoveItem(id, existingItem.id);
          }
        }

        // Add new items (temp IDs)
        for (const item of items) {
          if (!existingItemIds.has(item.id) || item.id.startsWith('temp-')) {
            const dto: AddPackageItemDTO = {
              itemType: item.itemType,
              productId: item.itemType === 'PRODUCT' ? item.referenceId : undefined,
              rentalEquipmentId:
                item.itemType === 'RENTAL_EQUIPMENT' ? item.referenceId : undefined,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              sortOrder: item.sortOrder,
              // STORY-1205 — csomag-tétel editor mezők (ADR-118 Am.2 D-1)
              category: item.category,
              defaultQuantity: item.defaultQuantity,
              isQuantityEditable: item.isQuantityEditable,
              visibleOnWebshop: item.visibleOnWebshop,
            };
            await doAddItem(id, dto);
          }
        }

        // Update existing items whose editor fields changed (P1-fix: meglévő-item-szerkesztés
        // perzisztálás). A nem-temp, megmaradó itemekre diff-PATCH a szerkeszthető mezőkre;
        // a sortOrder az aktuális tömb-indexet tükrözi (handleMoveItem reorder → mentett sorrend).
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (!item || item.id.startsWith('temp-')) continue; // új item — a doAddItem fent kezeli
          const original = existingApiItems.find(e => e.id === item.id);
          if (!original) continue;
          const patch: UpdatePackageItemDTO = {};
          if (item.category !== (original.category ?? 'RENTABLE')) patch.category = item.category;
          if (item.visibleOnWebshop !== (original.visibleOnWebshop ?? false))
            patch.visibleOnWebshop = item.visibleOnWebshop;
          if (item.defaultQuantity !== (original.defaultQuantity ?? 1))
            patch.defaultQuantity = item.defaultQuantity;
          if (item.isQuantityEditable !== (original.isQuantityEditable ?? false))
            patch.isQuantityEditable = item.isQuantityEditable;
          if (item.quantity !== original.quantity) patch.quantity = item.quantity;
          if (item.unitPrice !== (original.unitPrice ?? 0)) patch.unitPrice = item.unitPrice;
          if (i !== original.sortOrder) patch.sortOrder = i;
          if (Object.keys(patch).length > 0) {
            await doUpdateItem(id, item.id, patch);
          }
        }

        navigate(`/packages/${id}`);
      } else {
        // Create
        const createData: CreatePackageDTO = {
          packageCode: form.code,
          name: form.name,
          description: form.description || undefined,
          type: form.type,
          scope: form.scope,
          pricingModel: form.pricingModel,
          fixedPrice: form.fixedPrice ? parseFloat(form.fixedPrice) : undefined,
          discountPercent: form.discountPercent ? parseFloat(form.discountPercent) : undefined,
          validFrom: form.validFrom || undefined,
          validTo: form.validUntil || undefined,
          linkingMode: form.type === 'RENTAL' ? form.linkingMode : undefined,
          linkedEquipmentCategories:
            form.type === 'RENTAL' && form.linkingMode === 'CATEGORY'
              ? form.linkedEquipmentCategories
              : [],
          linkedProductIds:
            form.type === 'RENTAL' && form.linkingMode === 'EQUIPMENT'
              ? form.linkedEquipment.map(e => e.productId)
              : [],
        };
        const newPkg = await doCreate(createData);

        // Fix #5: When copying, persist the items from the source package to the new package
        if (newPkg?.id && items.length > 0) {
          for (const item of items) {
            const dto: AddPackageItemDTO = {
              itemType: item.itemType,
              productId: item.itemType === 'PRODUCT' ? item.referenceId : undefined,
              rentalEquipmentId:
                item.itemType === 'RENTAL_EQUIPMENT' ? item.referenceId : undefined,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              sortOrder: item.sortOrder,
              // STORY-1205 — csomag-tétel editor mezők (ADR-118 Am.2 D-1)
              category: item.category,
              defaultQuantity: item.defaultQuantity,
              isQuantityEditable: item.isQuantityEditable,
              visibleOnWebshop: item.visibleOnWebshop,
            };
            await doAddItem(newPkg.id, dto);
          }
        }

        navigate('/packages');
      }
    } catch {
      // Error is handled in hooks
    } finally {
      setIsSyncing(false);
    }
  };

  const isSaving = createLoading || updateLoading;

  if (pkgLoading && loadId) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/packages')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {isEditMode ? t('packages.editPackage') : t('packages.createNew')}
              </h1>
              <p className="text-sm text-muted-foreground">{t('packages.editorSubtitle')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate('/packages')}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={isSaving || isSyncing || !form.name || !form.code}
              className="bg-primary hover:bg-primary/90"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* STORY-1206 Q7 — folyamatos shared-banner (információ; a save-dialog a döntés) */}
        {sharedEquipmentCount >= 2 && (
          <Alert
            data-testid="shared-package-banner"
            className="mb-6 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30"
          >
            <AlertDescription className="text-amber-900 dark:text-amber-100">
              {t('packages.editor.sharedBanner', { count: sharedEquipmentCount })}
            </AlertDescription>
          </Alert>
        )}
        {/* Basic Info */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              {t('packages.basicInfo')}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('packages.packageName')} *
                </label>
                <Input
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  placeholder={t('packages.packageNamePlaceholder')}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('packages.packageCode')} *
                </label>
                <Input
                  value={form.code}
                  onChange={e => setField('code', e.target.value)}
                  placeholder={t('packages.packageCodePlaceholder')}
                  disabled={isEditMode}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('common.type')}
                </label>
                <select
                  value={form.type}
                  onChange={e => setField('type', e.target.value as PackageType)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-foreground dark:border-slate-600 dark:bg-slate-800"
                >
                  {PACKAGE_TYPES.map(type => (
                    <option key={type} value={type}>
                      {t(`packages.type.${type.toLowerCase()}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('packages.scopeLabel')}
                </label>
                <select
                  value={form.scope}
                  onChange={e => setField('scope', e.target.value as PackageScope)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-foreground dark:border-slate-600 dark:bg-slate-800"
                >
                  {SCOPES.map(scope => (
                    <option key={scope} value={scope}>
                      {t(`packages.scopeValue.${scope.toLowerCase()}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Package scope — only for RENTAL packages */}
            {form.type === 'RENTAL' && (
              <PackageScopeSection
                linkingMode={form.linkingMode}
                categories={form.linkedEquipmentCategories}
                equipment={form.linkedEquipment}
                onChange={next => {
                  setForm(prev => ({
                    ...prev,
                    linkingMode: next.linkingMode,
                    linkedEquipmentCategories: next.categories,
                    linkedEquipment: next.equipment,
                  }));
                }}
              />
            )}

            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-foreground">
                {t('common.description')}
              </label>
              <textarea
                value={form.description}
                onChange={e => setField('description', e.target.value)}
                placeholder={t('packages.descriptionPlaceholder')}
                rows={2}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-foreground dark:border-slate-600 dark:bg-slate-800"
              />
            </div>
          </CardContent>
        </Card>

        {/* Two-Panel Layout: Items + Product Search */}
        <div className="mb-6 grid gap-6 lg:grid-cols-2">
          {/* Left: Package Items */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="mb-4 text-lg font-semibold text-foreground">
                {t('packages.packageItems')} ({items.length})
              </h2>
              {items.filter(i => availableItemTypes.includes(i.itemType)).length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">{t('packages.noItems')}</p>
              ) : (
                <Accordion
                  type="multiple"
                  defaultValue={['FREE', 'RENTABLE', 'CONSIGNMENT']}
                  className="space-y-2"
                >
                  {(['FREE', 'RENTABLE', 'CONSIGNMENT'] as const).map(cat => {
                    const groupItems = items
                      .filter(i => availableItemTypes.includes(i.itemType))
                      .map((item, idx) => ({ item, idx }))
                      .filter(({ item }) => item.category === cat);
                    return (
                      <AccordionItem
                        key={cat}
                        value={cat}
                        data-testid={`package-category-group-${cat}`}
                        className="rounded-lg border bg-background dark:border-slate-700"
                      >
                        <AccordionTrigger className="px-3 py-2 hover:no-underline">
                          <span className="flex flex-1 items-center justify-between pr-2">
                            <span className="font-medium text-foreground">
                              {t(`packages.item.categoryOption.${cat.toLowerCase()}`)}
                            </span>
                            <span
                              data-testid={`package-category-count-${cat}`}
                              className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                            >
                              {groupItems.length}
                            </span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="px-3 pb-3 pt-1">
                          {groupItems.length === 0 ? (
                            <p className="py-3 text-center text-sm text-muted-foreground">
                              {t('packages.noItems')}
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {groupItems.map(({ item, idx }) => (
                                <div
                                  key={item.id}
                                  className="rounded-lg border p-3 dark:border-slate-700"
                                >
                                  <div className="flex items-center gap-3">
                                    {/* Sort controls */}
                                    <div className="flex flex-col gap-0.5">
                                      <button
                                        onClick={() => handleMoveItem(item.id, 'up')}
                                        disabled={idx === 0}
                                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                        aria-label={t('packages.moveUp')}
                                      >
                                        <svg
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 15l7-7 7 7"
                                          />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => handleMoveItem(item.id, 'down')}
                                        disabled={idx === items.length - 1}
                                        className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                                        aria-label={t('packages.moveDown')}
                                      >
                                        <svg
                                          className="h-4 w-4"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                          />
                                        </svg>
                                      </button>
                                    </div>

                                    {/* Item info */}
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        <Badge className={ITEM_TYPE_COLORS[item.itemType]}>
                                          {t(`packages.itemType.${item.itemType.toLowerCase()}`)}
                                        </Badge>
                                      </div>
                                      <p className="mt-1 font-medium text-foreground">
                                        {item.referenceName}
                                      </p>
                                      {item.referenceSku && (
                                        <p className="text-xs text-muted-foreground">
                                          {item.referenceSku}
                                        </p>
                                      )}
                                    </div>

                                    {/* Quantity */}
                                    <div className="w-16 shrink-0">
                                      <Input
                                        type="number"
                                        min={1}
                                        value={item.quantity}
                                        onChange={e =>
                                          handleUpdateItemQty(
                                            item.id,
                                            parseInt(e.target.value) || 1
                                          )
                                        }
                                        className="text-center"
                                      />
                                    </div>

                                    {/* Unit price display */}
                                    <div className="w-20 shrink-0 text-right text-sm text-muted-foreground">
                                      {formatHuf(item.unitPrice)}
                                    </div>

                                    {/* Remove */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-600 hover:text-red-700 dark:text-red-400"
                                      onClick={() => handleRemoveItem(item.id)}
                                    >
                                      {t('common.remove')}
                                    </Button>
                                  </div>

                                  {/* STORY-1205 — csomag-tétel editor mezők (ADR-118 Am.2 D-1) */}
                                  <div className="mt-3 flex flex-wrap items-end gap-4 border-t pt-3 dark:border-slate-700">
                                    <div className="w-40">
                                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                        {t('packages.item.category')}
                                      </label>
                                      <select
                                        value={item.category}
                                        onChange={e =>
                                          handleUpdateItemField(
                                            item.id,
                                            'category',
                                            e.target.value as PackageItemCategory
                                          )
                                        }
                                        className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-foreground dark:border-slate-600 dark:bg-slate-800"
                                      >
                                        {(['FREE', 'RENTABLE', 'CONSIGNMENT'] as const).map(c => (
                                          <option key={c} value={c}>
                                            {t(`packages.item.categoryOption.${c.toLowerCase()}`)}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    <div className="w-28">
                                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                                        {t('packages.item.defaultQuantity')}
                                      </label>
                                      <Input
                                        type="number"
                                        min={1}
                                        value={item.defaultQuantity}
                                        onChange={e =>
                                          handleUpdateItemField(
                                            item.id,
                                            'defaultQuantity',
                                            Math.max(1, parseInt(e.target.value) || 1)
                                          )
                                        }
                                        className="text-center"
                                      />
                                    </div>

                                    <label className="flex items-center gap-2 pb-1.5 text-sm text-foreground">
                                      <input
                                        type="checkbox"
                                        checked={item.isQuantityEditable}
                                        onChange={e =>
                                          handleUpdateItemField(
                                            item.id,
                                            'isQuantityEditable',
                                            e.target.checked
                                          )
                                        }
                                        className="h-4 w-4 rounded border-gray-300"
                                      />
                                      {t('packages.item.isQuantityEditable')}
                                    </label>

                                    {/* CONSIGNMENT-nél disabled — a DB-trigger FALSE-ra forceolja (ADR-118 Am.2 D-4) */}
                                    <label className="flex items-center gap-2 pb-1.5 text-sm text-foreground">
                                      <input
                                        type="checkbox"
                                        checked={item.visibleOnWebshop}
                                        disabled={item.category === 'CONSIGNMENT'}
                                        onChange={e =>
                                          handleUpdateItemField(
                                            item.id,
                                            'visibleOnWebshop',
                                            e.target.checked
                                          )
                                        }
                                        className="h-4 w-4 rounded border-gray-300 disabled:opacity-40"
                                      />
                                      <span
                                        className={
                                          item.category === 'CONSIGNMENT' ? 'opacity-40' : ''
                                        }
                                      >
                                        {t('packages.item.visibleOnWebshop')}
                                      </span>
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </CardContent>
          </Card>

          {/* Right: Product Search */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="mb-4 text-lg font-semibold text-foreground">
                {t('packages.productSearch')}
              </h2>
              <Input
                placeholder={t('packages.searchProductPlaceholder')}
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                className="mb-4"
              />

              {searchLoading && (
                <p className="py-4 text-center text-muted-foreground">{t('common.loading')}</p>
              )}

              {!searchLoading && productSearch && searchResults.length === 0 && (
                <p className="py-4 text-center text-muted-foreground">
                  {t('packages.noSearchResults')}
                </p>
              )}

              <div className="max-h-[500px] space-y-2 overflow-y-auto">
                {searchResults.map(product => {
                  const alreadyAdded = items.some(i => i.referenceId === product.id);
                  return (
                    <div
                      key={product.id}
                      className={`flex items-center justify-between rounded-lg border p-3 dark:border-slate-700 ${
                        alreadyAdded
                          ? 'opacity-50'
                          : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50'
                      }`}
                      onClick={() => !alreadyAdded && handleAddProduct(product)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-foreground">{product.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {product.sku}
                          {product.brand ? ` - ${product.brand}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">
                          {formatHuf(product.sellingPriceGross)}
                        </span>
                        {alreadyAdded ? (
                          <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {t('packages.alreadyAdded')}
                          </Badge>
                        ) : (
                          <Button variant="ghost" size="sm">
                            + {t('common.add')}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Section */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold text-foreground">{t('packages.pricing')}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('packages.pricingModelLabel')}
                </label>
                <select
                  value={form.pricingModel}
                  onChange={e => setField('pricingModel', e.target.value as PricingModel)}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-foreground dark:border-slate-600 dark:bg-slate-800"
                >
                  {PRICING_MODELS.map(model => (
                    <option key={model} value={model}>
                      {t(`packages.pricingModel.${model.toLowerCase()}`)}
                    </option>
                  ))}
                </select>
              </div>

              {form.pricingModel === 'FIXED' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t('packages.fixedPriceLabel')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={form.fixedPrice}
                    onChange={e => setField('fixedPrice', e.target.value)}
                    placeholder="0"
                  />
                </div>
              )}

              {form.pricingModel === 'DISCOUNTED' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    {t('packages.discountPercentLabel')}
                  </label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={form.discountPercent}
                      onChange={e => setField('discountPercent', e.target.value)}
                      placeholder="0"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('packages.validFrom')}
                </label>
                <Input
                  type="date"
                  value={form.validFrom}
                  onChange={e => setField('validFrom', e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('packages.validUntil')}
                </label>
                <Input
                  type="date"
                  value={form.validUntil}
                  onChange={e => setField('validUntil', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live Calculator */}
        <Card className="mb-6 border-2 border-primary/20">
          <CardContent className="pt-6">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              {t('packages.calculator')}
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">{t('packages.itemsTotal')}</p>
                <p className="text-2xl font-bold text-foreground">
                  {formatHuf(calculator.itemsTotal)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">{t('packages.packagePriceLabel')}</p>
                <p className="text-2xl font-bold text-primary">
                  {formatHuf(calculator.packagePrice)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">{t('packages.savingsLabel')}</p>
                <p
                  className={`text-2xl font-bold ${
                    calculator.savings > 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-foreground'
                  }`}
                >
                  {calculator.savings > 0 ? '-' : ''}
                  {Math.round(calculator.savings)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* STORY-1206 Q4 — detect-first conflict dialog + stage-2 gép-picker (#412 fork) */}
      <SharedPackageConflictDialog
        open={conflictOpen}
        sharedEquipmentCount={sharedEquipmentCount}
        onResolve={handleConflictResolve}
        onOpenChange={setConflictOpen}
        copyTargets={(existingPkg?.linkedProducts ?? []).map(lp => ({
          id: lp.productId,
          label: `${lp.product.name} (${lp.product.sku})`,
        }))}
        selectedCopyTargetId={copyTargetId}
        onCopyTargetChange={setCopyTargetId}
        labels={{
          title: t('packages.editor.conflictTitle'),
          bannerText: count => t('packages.editor.sharedBanner', { count }),
          bannerSubtext: t('packages.editor.conflictSubtext'),
          applyToAllLabel: t('packages.editor.conflictApplyAll'),
          applyToAllDesc: t('packages.editor.conflictApplyAllDesc'),
          copyForThisLabel: t('packages.editor.conflictCopyForThis'),
          copyForThisDesc: t('packages.editor.conflictCopyForThisDesc'),
          loadingCopyForThis: t('packages.editor.conflictForking'),
          copyTargetPlaceholder: t('packages.editor.conflictCopyTargetPlaceholder'),
          copyTargetAriaLabel: t('packages.editor.conflictCopyTargetAriaLabel'),
          cancelLabel: t('common.cancel'),
          loadingApplyToAll: t('common.saving'),
        }}
      />
    </div>
  );
}
